import React, { useCallback, useState } from 'react';
import { CircularProgress, Grid, Link, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { AccountData, PassOut, PassService, PassStatus } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { PassCard } from './PassCard';

type PassSearchPanelProps = {
  selectedPass: PassOut | undefined;
  onSelectPass: (pass: PassOut | undefined) => void;
};

export const PassSearchPanel = ({ selectedPass, onSelectPass }: PassSearchPanelProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<PassOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = useCallback(
    debounce(async (query: string) => {
      if (!query || query.length < 2) {
        setResults([]);
        setSearched(false);
        return;
      }
      setLoading(true);
      try {
        const passes = await PassService.getPasses(1, 10, query);
        setResults(passes.filter((pass) => pass.status !== PassStatus.ARCHIVED));
        setSearched(true);
      } finally {
        setLoading(false);
      }
    }, 400),
    [],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    void search(e.target.value);
  };

  const handleClear = () => {
    onSelectPass(undefined);
    setSearchQuery('');
    setResults([]);
    setSearched(false);
  };

  if (selectedPass) {
    return (
      <Grid container flexDirection="column" gap={1}>
        <PassCard pass={selectedPass} selected onClick={() => {}} account={account} language={language} />
        <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }} onClick={handleClear}>
          {t('passes.change_pass')}
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid container flexDirection="column" gap={1}>
      <TextField fullWidth placeholder={t('passes.search_placeholder')} value={searchQuery} onChange={handleSearch} />
      {loading && (
        <Grid container justifyContent="center" p={1}>
          <CircularProgress size={24} />
        </Grid>
      )}
      {!loading && results.length > 0 && (
        <Grid container flexDirection="column" gap={1}>
          {results.map((pass) => (
            <PassCard
              key={pass.id}
              pass={pass}
              selected={false}
              onClick={() => onSelectPass(pass)}
              account={account}
              language={language}
            />
          ))}
        </Grid>
      )}
      {!loading && searched && results.length === 0 && (
        <Grid container gap={1}>
          <Typography variant="body2" color="text.secondary">
            {t('passes.no_passes_found')}
          </Typography>
          <Link component="button" onClick={() => navigate('/dashboard/passes?create=true')}>
            <Typography variant="body2" color="text.secondary">
              {t('passes.create_pass_link')}
            </Typography>
          </Link>
        </Grid>
      )}
    </Grid>
  );
};
